// eslint-disable-next-line import/no-extraneous-dependencies
import dagre from '@dagrejs/dagre';
import { type Edge, type Node, Position } from '@xyflow/react';
import { type BaseNodeData } from '../types';
import { NODE_VERTICAL_SPACING } from './layoutConstants';

// Vertical layout configuration
const VERTICAL_LAYOUT_CONFIG = {
	direction: 'TB' as const,
	nodeSpacing: 250, // Horizontal spacing between sibling nodes
	rankSpacing: 180, // Vertical spacing between ranks
};

const DEFAULT_NODE_HEIGHT = 120;
const _DEFAULT_NODE_WIDTH = 250;

/**
 * Type guard to check if node data has estimatedHeight property
 */
function hasEstimatedHeight(data: BaseNodeData): data is BaseNodeData & { estimatedHeight: number } {
	return typeof (data as { estimatedHeight?: unknown }).estimatedHeight === 'number';
}

function getNodeHeight(node: Node<BaseNodeData>): number {
	if (typeof node.height === 'number') {
		return node.height;
	}

	if (hasEstimatedHeight(node.data)) {
		return node.data.estimatedHeight;
	}

	return DEFAULT_NODE_HEIGHT;
}

export function applyLayout(
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): { nodes: Node<BaseNodeData>[]; edges: Edge[] } {
	const config = VERTICAL_LAYOUT_CONFIG;
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));

	dagreGraph.setGraph({
		rankdir: config.direction,
		nodesep: config.nodeSpacing,
		ranksep: config.rankSpacing,
		ranker: 'network-simplex',
	});

	nodes.forEach(node => {
		dagreGraph.setNode(node.id, {
			width: 250,
			height: 120,
		});
	});

	edges.forEach(edge => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	const layoutedNodesWithPositions: Node<BaseNodeData>[] = nodes.map(node => {
		const nodeWithPosition = dagreGraph.node(node.id);

		return {
			...node,
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			data: {
				...node.data,
				targetPosition: Position.Top,
				sourcePosition: Position.Bottom,
			},
			position: {
				x: nodeWithPosition.x - 125,
				y: nodeWithPosition.y - 60,
			},
		};
	});

	const layoutedNodes = applySwitchBranchLayout(layoutedNodesWithPositions, edges);

	return { nodes: layoutedNodes, edges };
}

/**
 * Represents metadata about a switch node and its branches
 */
interface SwitchNodeMetadata {
	switchNodeId: string;
	branches: Map<string, string[]>; // handleId -> array of node IDs in that branch
}

/**
 * Type guard to check if node data represents a switch step
 */
function isSwitchNodeData(data: BaseNodeData): data is BaseNodeData & {
	automationStep: { _type: string };
	outputHandles: string[];
} {
	const nodeData = data as {
		automationStep?: { _type?: string };
		outputHandles?: unknown[];
	};
	return (
		nodeData.automationStep !== undefined &&
		nodeData.automationStep._type === 'SwitchAutomationStep' &&
		Array.isArray(nodeData.outputHandles) &&
		nodeData.outputHandles.length > 1
	);
}

/**
 * Identifies switch nodes and their branches in the flow.
 * Returns metadata about each switch node including which nodes belong to which branch.
 *
 * @param nodes - All nodes in the flow
 * @param edges - All edges in the flow
 * @returns Array of switch node metadata
 */
function identifySwitchBranches(nodes: Node<BaseNodeData>[], edges: Edge[]): SwitchNodeMetadata[] {
	const switchMetadata: SwitchNodeMetadata[] = [];

	// Find all switch nodes
	const switchNodes = nodes.filter(node => isSwitchNodeData(node.data));

	for (const switchNode of switchNodes) {
		const nodeData = switchNode.data;
		const outputHandles = nodeData.outputHandles || [];
		const branches = new Map<string, string[]>();

		// For each output handle (branch), find all descendant nodes
		for (const handleId of outputHandles) {
			const branchNodeIds: string[] = [];
			const visited = new Set<string>();
			const queue: string[] = [];

			// Find direct children of this branch
			const directChildren = edges.filter(edge => edge.source === switchNode.id && edge.sourceHandle === handleId);

			for (const edge of directChildren) {
				queue.push(edge.target);
				visited.add(edge.target);
			}

			// Find all descendants using BFS
			while (queue.length > 0) {
				const currentNodeId = queue.shift()!;
				branchNodeIds.push(currentNodeId);

				// Find children of current node (but stop at other switch nodes)
				const currentNode = nodes.find(n => n.id === currentNodeId);
				if (!currentNode) {
					continue;
				}

				const isAnotherSwitch = isSwitchNodeData(currentNode.data);

				if (!isAnotherSwitch) {
					const childEdges = edges.filter(edge => edge.source === currentNodeId);
					for (const edge of childEdges) {
						if (!visited.has(edge.target)) {
							visited.add(edge.target);
							queue.push(edge.target);
						}
					}
				}
			}

			branches.set(handleId, branchNodeIds);
		}

		switchMetadata.push({
			switchNodeId: switchNode.id,
			branches,
		});
	}

	return switchMetadata;
}

/**
 * Applies lane-based layout for switch node branches in vertical layout.
 * Each branch gets its own horizontal lane (column).
 *
 * @param nodes - All nodes in the flow
 * @param edges - All edges in the flow
 * @returns Nodes with updated positions for lane-based layout
 */
export function applySwitchBranchLayout(nodes: Node<BaseNodeData>[], edges: Edge[]): Node<BaseNodeData>[] {
	// Defensive check: ensure inputs are valid
	if (!nodes || nodes.length === 0) {
		return nodes;
	}

	if (!edges || edges.length === 0) {
		return nodes;
	}

	const switchMetadata = identifySwitchBranches(nodes, edges);

	if (switchMetadata.length === 0) {
		return nodes;
	}

	const updatedNodes = [...nodes];
	const nodeIndexMap = new Map<string, number>();
	updatedNodes.forEach((node, index) => {
		nodeIndexMap.set(node.id, index);
	});
	const positionedNodes = new Set<string>();
	const getNodeById = (nodeId: string) => {
		const nodeIndex = nodeIndexMap.get(nodeId);
		if (nodeIndex === undefined) {
			return undefined;
		}
		return updatedNodes[nodeIndex];
	};

	for (const metadata of switchMetadata) {
		const switchNodeIndex = updatedNodes.findIndex(n => n.id === metadata.switchNodeId);
		if (switchNodeIndex === -1) {
			continue;
		}

		const switchNode = updatedNodes[switchNodeIndex];

		// Defensive check: ensure switch node has valid position
		if (
			!switchNode.position ||
			typeof switchNode.position.x !== 'number' ||
			typeof switchNode.position.y !== 'number'
		) {
			continue;
		}

		const branches = Array.from(metadata.branches.entries());
		if (branches.length === 0) {
			continue;
		}

		updatedNodes[switchNodeIndex] = {
			...switchNode,
			sourcePosition: Position.Bottom,
			data: {
				...switchNode.data,
				sourcePosition: Position.Bottom,
			},
		};

		const switchNodeHeight = getNodeHeight(switchNode);
		const branchStartY = switchNode.position.y + switchNodeHeight + NODE_VERTICAL_SPACING;

		const directChildrenByHandle = new Map<string, Set<string>>();
		for (const [handleId] of branches) {
			const directChildren = edges
				.filter(edge => edge.source === switchNode.id && edge.sourceHandle === handleId)
				.map(edge => edge.target);
			directChildrenByHandle.set(handleId, new Set(directChildren));
		}

		for (const [handleId, branchNodeIds] of branches) {
			if (!branchNodeIds || branchNodeIds.length === 0) {
				continue;
			}

			const branchNodes = branchNodeIds
				.map(nodeId => getNodeById(nodeId))
				.filter(
					(node): node is Node<BaseNodeData> =>
						node !== undefined && typeof node.position?.x === 'number' && typeof node.position?.y === 'number'
				);

			if (branchNodes.length === 0) {
				continue;
			}

			const directChildIds = directChildrenByHandle.get(handleId) ?? new Set<string>();
			const directChildNodes = branchNodes.filter(node => directChildIds.has(node.id));
			if (directChildNodes.length === 0) {
				continue;
			}

			const minDirectChildY = Math.min(...directChildNodes.map(node => node.position.y));
			const shiftY = Math.max(0, branchStartY - minDirectChildY);

			if (shiftY === 0) {
				continue;
			}

			for (const branchNode of branchNodes) {
				if (positionedNodes.has(branchNode.id)) {
					continue;
				}

				const nodeIndex = nodeIndexMap.get(branchNode.id);
				if (nodeIndex === undefined) {
					continue;
				}

				updatedNodes[nodeIndex] = {
					...branchNode,
					position: {
						x: branchNode.position.x,
						y: branchNode.position.y + shiftY,
					},
				};

				positionedNodes.add(branchNode.id);
			}
		}
	}

	return updatedNodes;
}
